import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { app, db } from '@/firebase/firebase';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { Button } from '@/components/button';
import { toast } from 'react-hot-toast';

/**
 * TeamLogoUploader
 * - Uploads an image to Storage: teams/{teamId}/branding/
 * - Generates square PNGs (512 & 128) from the selected image
 * - Saves Firestore fields: logoUrl, logo128Url, branding.logoUrl, updatedAt
 *
 * Props:
 *  - teamId (required)
 *  - value (optional)      : current logo URL to preview
 *  - onChange(url) (opt)   : fires with the new 512px URL after upload
 *  - onUploadingChange(bool) (opt)
 */
export default function TeamLogoUploader({
  teamId,
  value,
  onChange,
  onUploadingChange,
  className = '',
}) {
  const { i18n } = useTranslation();
  // Bilingual since 2026-07-24 — this uploader sits inside the (now
  // Spanish-capable) team wizard and settings pages.
  const U = String(i18n?.language || 'en').toLowerCase().startsWith('es')
    ? {
        missingTeam: 'Falta el ID del equipo.', updated: '¡Logo actualizado!',
        uploadFailed: 'No se pudo subir el logo', removed: 'Logo eliminado',
        removeFailed: 'No se pudo eliminar el logo', noLogo: 'Sin logo',
        uploading: 'Subiendo…', replace: 'Cambiar logo', upload: 'Subir logo',
        remove: 'Quitar',
        help: 'PNG/JPG/WebP/SVG. Lo recortamos en cuadrado y lo optimizamos. Se recomiendan 2–5MB máx.',
      }
    : {
        missingTeam: 'Missing team id.', updated: 'Logo updated!',
        uploadFailed: 'Failed to upload logo', removed: 'Logo removed',
        removeFailed: 'Could not remove logo', noLogo: 'No logo',
        uploading: 'Uploading…', replace: 'Replace Logo', upload: 'Upload Logo',
        remove: 'Remove',
        help: 'PNG/JPG/WebP/SVG. We’ll crop to a square and optimize. 2–5MB max recommended.',
      };
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(value || '');
  const [uploading, setUploading] = useState(false);

  const storage = getStorage(app);

  function openPicker() {
    inputRef.current?.click();
  }

  function fileToImage(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => resolve({ img, url });
      img.onerror = reject;
      img.src = url;
    });
  }

  // cover-crop square -> canvas -> Blob PNG
  async function rasterizeSquarePNG(file, size = 512) {
    const { img, url } = await fileToImage(file);
    const s = Math.min(img.naturalWidth, img.naturalHeight);
    const sx = Math.floor((img.naturalWidth - s) / 2);
    const sy = Math.floor((img.naturalHeight - s) / 2);

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, sx, sy, s, s, 0, 0, size, size);

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          resolve(blob);
        },
        'image/png',
        0.92
      );
    });
  }

  async function doUpload(file) {
    if (!teamId) {
      toast.error(U.missingTeam);
      return;
    }
    try {
      setUploading(true);
      onUploadingChange?.(true);

      // For SVGs: just upload original (no rasterization)
      const isSvg = file.type === 'image/svg+xml';

      let url512 = '';
      let url128 = '';

      if (isSvg) {
        const origRef = ref(storage, `teams/${teamId}/branding/logo.svg`);
        await uploadBytes(origRef, file, { contentType: 'image/svg+xml' });
        url512 = await getDownloadURL(origRef);
        url128 = url512; // same (rasterization skipped)
      } else {
        // 512
        const blob512 = await rasterizeSquarePNG(file, 512);
        const ref512 = ref(storage, `teams/${teamId}/branding/logo-512.png`);
        await uploadBytes(ref512, blob512, { contentType: 'image/png' });
        url512 = await getDownloadURL(ref512);

        // 128
        const blob128 = await rasterizeSquarePNG(file, 128);
        const ref128 = ref(storage, `teams/${teamId}/branding/logo-128.png`);
        await uploadBytes(ref128, blob128, { contentType: 'image/png' });
        url128 = await getDownloadURL(ref128);
      }

      // Persist in Firestore
      await setDoc(
        doc(db, 'teams', teamId),
        {
          logoUrl: url512,
          logo128Url: url128,
          branding: { logoUrl: url512 },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setPreview(url512);
      onChange?.(url512);
      toast.success(U.updated);
    } catch (e) {
      console.error('[TeamLogoUploader] upload failed', e);
      toast.error(U.uploadFailed);
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
    }
  }

  async function removeLogo() {
    if (!teamId) return;
    try {
      setUploading(true);
      onUploadingChange?.(true);

      // Best-effort deletes
      const p512 = ref(storage, `teams/${teamId}/branding/logo-512.png`);
      const p128 = ref(storage, `teams/${teamId}/branding/logo-128.png`);
      await Promise.allSettled([deleteObject(p512), deleteObject(p128)]);

      await setDoc(
        doc(db, 'teams', teamId),
        {
          logoUrl: null,
          logo128Url: null,
          branding: { logoUrl: null },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setPreview('');
      onChange?.('');
      toast.success(U.removed);
    } catch (e) {
      console.warn('[TeamLogoUploader] remove failed', e);
      toast.error(U.removeFailed);
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
    }
  }

  return (
    <div className={`flex items-start gap-4 ${className}`}>
      <div className="w-20 h-20 rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 grid place-items-center overflow-hidden">
        {preview ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={preview} alt="Team logo" className="w-full h-full object-cover" />
        ) : (
          <span className="text-xs text-zinc-500">{U.noLogo}</span>
        )}
      </div>

      <div className="flex-1 space-y-2">
        <div className="flex gap-2">
          <Button type="button" onClick={openPicker} disabled={uploading}>
            {uploading ? U.uploading : (preview ? U.replace : U.upload)}
          </Button>
          {preview ? (
            <Button type="button" variant="secondary" onClick={removeLogo} disabled={uploading}>
              {U.remove}
            </Button>
          ) : null}
        </div>
        <p className="text-xs text-zinc-500">
          {U.help}
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          doUpload(f);
          // temp preview
          const reader = new FileReader();
          reader.onload = () => setPreview(String(reader.result));
          reader.readAsDataURL(f);
          e.target.value = '';
        }}
      />
    </div>
  );
}
